import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { useHistory, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "components/Common/Breadcrumb";
import { VendorAccountingApi } from "apis/VendorAccountingApi";
import moment from "moment";
import Swal from "../../helpers/swal_helper";
import UserSelect from "components/Common/UserSelect";

function AddEditVendorAccounting(props) {
    const history = useHistory();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [initialValues, setInitialValues] = useState({
        booking_id: "",
        vendor_id: null,
        final_bill_amount: "",
        paid_via: "Online",
        booking_status: "Waiting",
        is_settled: false,
        settled_date: moment().format("YYYY-MM-DD")
    });

    const validationSchema = Yup.object().shape({
        booking_id: Yup.number()
            .required(props.t("Booking ID is required"))
            .min(1, props.t("Booking ID must be greater than 0")),
        vendor_id: Yup.number()
            .required(props.t("Vendor is required")),
        final_bill_amount: Yup.number()
            .required(props.t("Final bill amount is required"))
            .min(0, props.t("Final bill amount must be greater than or equal to 0")),
        paid_via: Yup.string()
            .required(props.t("Paid via is required")),
        booking_status: Yup.string()
            .required(props.t("Booking status is required")),
        is_settled: Yup.string(),
        // settled_date: Yup.date()
        //     .when('is_settled', {
        //         is: "true",
        //         then: Yup.date().required(props.t("Settled date is required when settled is true"))
        //     })
    });

    useEffect(() => {
        if (id) {
            getVendorAccountingById(id);
        }
    }, [id]);

    const getVendorAccountingById = (id) => {
        setLoading(true);
        VendorAccountingApi.getVendorAccountingById({
            vendor_accounting_id: id
        }).then((res) => {
            if (res.data.status) {
                const data = res.data.data;
                setSelectedUser({
                    value: data.vendor_id,
                    label: `${data.vendor?.firstname} ${data.vendor?.lastname}`
                })
                setInitialValues({
                    ...data,
                    vendor_id: data.vendor_id || null,
                    settled_date: moment(data.settled_date).format("YYYY-MM-DD"),
                    is_settled: data.is_settled ? "true" : "false",
                    booking_id: data.booking_id,
                    final_bill_amount: data.final_bill_amount,
                    paid_via: data.paid_via,
                    booking_status: data.booking_status,

                });
            } else {
                Swal.fire({
                    title: "Error",
                    text: res.data.message,
                    icon: "error",
                });
            }
            setLoading(false);
        }).catch((err) => {
            console.log(err);
            setLoading(false);
            Swal.fire({
                title: "Error",
                text: "Failed to fetch vendor accounting details",
                icon: "error",
            });
        });
    };

    const handleSubmit = (values, { setSubmitting }) => {
        setLoading(true);

        const submitData = {
            ...values,
            vendor_id: values.vendor_id,
            is_settled: values.is_settled === "true" ? true : false
        };

        if (id) {
            submitData.vendor_accounting_id = id;
        }

        const apiCall = id
            ? VendorAccountingApi.updateVendorAccounting(submitData)
            : VendorAccountingApi.createVendorAccounting(submitData);

        apiCall
            .then((res) => {
                if (res.data.status) {
                    Swal.fire({
                        title: "Success",
                        text: res.data.message,
                        icon: "success",
                    }).then(() => {
                        history.push("/vendor-accountings");
                    });
                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.data.message,
                        icon: "error",
                    });
                }
                setLoading(false);
                setSubmitting(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                setSubmitting(false);
                Swal.fire({
                    title: "Error",
                    text: "Failed to save vendor accounting",
                    icon: "error",
                });
            });
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs
                    title={props.t("Dashboard")}
                    breadcrumbItem={id ? props.t("Edit Vendor Accounting") : props.t("Add Vendor Accounting")}
                />
            </div>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                    enableReinitialize
                                >
                                    {({ errors, touched, values, setFieldValue }) => (
                                        <Form>
                                            <div className="row mb-3">
                                                <div className="col-md-6 mt-2">
                                                    <label className="form-label">{props.t("Booking ID")}</label>
                                                    <Field
                                                        type="number"
                                                        className={`form-control ${errors.booking_id && touched.booking_id ? 'is-invalid' : ''}`}
                                                        name="booking_id"
                                                    />
                                                    {errors.booking_id && touched.booking_id && (
                                                        <div className="invalid-feedback">{errors.booking_id}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6 mt-2">
                                                    <label className="form-label">{props.t("Vendor")}</label>
                                                    <UserSelect
                                                        value={selectedUser}
                                                        defaultValue={selectedUser}
                                                        onChange={(e) => {
                                                            setFieldValue('vendor_id', e.value);
                                                            setSelectedUser(e);
                                                        }}
                                                        t={props.t}
                                                        isMulti={false}
                                                        role_id={2}
                                                    />
                                                    {errors.vendor_id && touched.vendor_id && (
                                                        <div className="invalid-feedback">{errors.vendor_id}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6 mt-2">
                                                    <label className="form-label">{props.t("Final Bill Amount")}</label>
                                                    <Field
                                                        type="number"
                                                        step="0.01"
                                                        className={`form-control ${errors.final_bill_amount && touched.final_bill_amount ? 'is-invalid' : ''}`}
                                                        name="final_bill_amount"
                                                    />
                                                    {errors.final_bill_amount && touched.final_bill_amount && (
                                                        <div className="invalid-feedback">{errors.final_bill_amount}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6 mt-2">
                                                    <label className="form-label">{props.t("Paid Via")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-control ${errors.paid_via && touched.paid_via ? 'is-invalid' : ''}`}
                                                        name="paid_via"
                                                    >
                                                        <option value="Online">{props.t("Online")}</option>
                                                        <option value="Cash">{props.t("Cash")}</option>
                                                        <option value="Card">{props.t("Card")}</option>
                                                    </Field>
                                                    {errors.paid_via && touched.paid_via && (
                                                        <div className="invalid-feedback">{errors.paid_via}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6 mt-2">
                                                    <label className="form-label">{props.t("Booking Status")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-control ${errors.booking_status && touched.booking_status ? 'is-invalid' : ''}`}
                                                        name="booking_status"
                                                    >
                                                        <option value="Waiting">{props.t("Waiting")}</option>
                                                        <option value="Confirmed">{props.t("Confirmed")}</option>
                                                        <option value="Rejected">{props.t("Rejected")}</option>
                                                        <option value="Completed">{props.t("Completed")}</option>
                                                        <option value="Canceled">{props.t("Canceled")}</option>
                                                        <option value="Started">{props.t("Started")}</option>
                                                    </Field>
                                                    {errors.booking_status && touched.booking_status && (
                                                        <div className="invalid-feedback">{errors.booking_status}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6 mt-2">
                                                    <label className="form-label">{props.t("Is Settled")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-select ${errors.is_settled && touched.is_settled ? 'is-invalid' : ''}`}
                                                        name="is_settled"
                                                    >
                                                        <option value={true}>Yes</option>
                                                        <option value={false}>No</option>
                                                    </Field>
                                                    {errors.is_settled && touched.is_settled && (
                                                        <div className="invalid-feedback">{errors.is_settled}</div>
                                                    )}
                                                </div>

                                                {values.is_settled === "true" && (
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">{props.t("Settled Date")}</label>
                                                        <Field
                                                            type="date"
                                                            className={`form-control ${errors.settled_date && touched.settled_date ? 'is-invalid' : ''}`}
                                                            name="settled_date"
                                                        />
                                                        {errors.settled_date && touched.settled_date && (
                                                            <div className="invalid-feedback">{errors.settled_date}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-12">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={loading}
                                                        // onClick={() => {
                                                        //     console.log('values', values);
                                                        //     console.log('errors', errors);
                                                        // }}
                                                    >
                                                        {loading ? props.t("Saving...") : props.t("Save")}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary ms-2"
                                                        onClick={() => history.push("/vendor-accountings")}
                                                    >
                                                        {props.t("Cancel")}
                                                    </button>
                                                </div>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

AddEditVendorAccounting.propTypes = {
    t: PropTypes.func,
};

export default withTranslation()(AddEditVendorAccounting); 